import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { type DocumentRowOut, listLabelingDocuments } from '@/api/labeling';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  type DocFilters,
  type DocSort,
  DEFAULT_SORT,
  EMPTY_FILTERS,
  FILE_TYPE_OPTIONS,
  type SortField,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
  nextSort,
  toDocumentQuery,
} from '@/labeling/documentFilters';

const PAGE_SIZE = 50;

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-CA'); // YYYY-MM-DD
}

export function BrowseTab({ getIdToken }: { getIdToken: () => Promise<string | null> }) {
  const [filters, setFilters] = useState<DocFilters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<DocSort>(DEFAULT_SORT);
  const [page, setPage] = useState(0);

  const query = useQuery({
    queryKey: ['labeling', 'documents', filters, sort, page],
    queryFn: async () =>
      listLabelingDocuments(
        toDocumentQuery(filters, sort, PAGE_SIZE, page * PAGE_SIZE),
        (await getIdToken()) ?? '',
      ),
    placeholderData: keepPreviousData,
  });

  const setFilter =
    <K extends keyof DocFilters>(key: K) =>
    (value: DocFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPage(0);
    };

  const sortBy = (field: SortField) => {
    setSort((prev) => nextSort(prev, field));
    setPage(0);
  };

  const total = query.data?.total ?? 0;
  const rows = query.data?.rows ?? [];
  const start = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const end = Math.min((page + 1) * PAGE_SIZE, total);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          label="File type"
          value={filters.file_type}
          options={FILE_TYPE_OPTIONS}
          onChange={setFilter('file_type')}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          options={STATUS_OPTIONS}
          onChange={setFilter('status')}
        />
        <FilterSelect
          label="Source"
          value={filters.source}
          options={SOURCE_OPTIONS}
          onChange={setFilter('source')}
        />
        <span className="ml-auto text-xs text-brand-slate/70">
          {total === 0 ? '' : `${start}–${end} of ${total}`}
        </span>
      </div>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
              <Spinner /> Loading documents…
            </div>
          ) : query.isError ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
              <span className="text-destructive">Couldn&apos;t load documents.</span>
              <Button type="button" variant="secondary" size="sm" onClick={() => query.refetch()}>
                Retry
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              No documents match these filters.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <SortHeader field="uploaded" label="Uploaded" sort={sort} onSort={sortBy} />
                  <SortHeader field="labeled" label="Labeled" sort={sort} onSort={sortBy} />
                  <TableHead className="text-right">Labels</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <DocumentRow key={row.document_id} row={row} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={page === 0 || query.isFetching}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={end >= total || query.isFetching}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function DocumentRow({ row }: { row: DocumentRowOut }) {
  // Only pending docs are claimable; the filename of a claimable row is a real
  // link to /labeling/$documentId, so Cmd/right-click opens it in a new tab and
  // a plain click navigates in place. Others render as non-actionable text.
  const claimable = row.status === 'pending';
  return (
    <TableRow className={claimable ? 'hover:bg-secondary/40' : undefined}>
      <TableCell className="max-w-[20rem]">
        {claimable ? (
          <Link
            to="/labeling/$documentId"
            params={{ documentId: row.document_id }}
            className="block max-w-full truncate text-left font-medium text-brand-royal hover:underline"
            title={row.filename}
          >
            {row.filename}
          </Link>
        ) : (
          <span className="block max-w-full truncate text-brand-navy" title={row.filename}>
            {row.filename}
          </span>
        )}
      </TableCell>
      <TableCell className="text-xs uppercase tracking-wide text-brand-slate">
        {row.file_type}
      </TableCell>
      <TableCell className="capitalize">
        {row.status}
        {row.skip_reason ? (
          <span className="ml-1 normal-case text-brand-slate/70">
            · {row.skip_reason.replace(/_/g, ' ')}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="text-brand-slate">{row.source}</TableCell>
      <TableCell className="tabular-nums text-brand-slate">{formatDate(row.created_at)}</TableCell>
      <TableCell className="tabular-nums text-brand-slate">{formatDate(row.labeled_at)}</TableCell>
      <TableCell className="text-right tabular-nums">
        {row.label_count}/{row.target_label_count}
      </TableCell>
    </TableRow>
  );
}

function SortHeader({
  field,
  label,
  sort,
  onSort,
}: {
  field: SortField;
  label: string;
  sort: DocSort;
  onSort: (field: SortField) => void;
}) {
  const active = sort.field === field;
  return (
    <TableHead aria-sort={active ? (sort.order === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="flex items-center gap-1 hover:text-brand-navy"
      >
        {label}
        {active ? <span className="text-xs">{sort.order === 'asc' ? '▲' : '▼'}</span> : null}
      </button>
    </TableHead>
  );
}

function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <label className="flex items-center gap-1 text-xs text-muted-foreground">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-brand-navy"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}
